"use client";

// src/state-handler.tsx
import * as React3 from "react";
import {
  Base,
  systemForwardRef
} from "@stepstone-genesis/system";

// src/context.ts
import * as React from "react";
var FormStateHandlerContext = React.createContext(null);
var useFormStateHandlerContext = () => {
  const context = React.useContext(FormStateHandlerContext);
  return context;
};

// src/hooks.tsx
import * as React2 from "react";
import { useUID } from "@stepstone-genesis/uid-library";
import { mergeRefs } from "@stepstone-genesis/hooks-use-merged-refs";
import { useToggle } from "@stepstone-genesis/hooks-use-toggle";
import { execAllFunctions, isFunction } from "@stepstone-genesis/utilities";
var useFormIdentifiers = (identifier) => {
  const uuid = useUID();
  const id = identifier != null ? identifier : `genesis-form-element-${uuid}`;
  const labelId = `${id}-label`;
  const feedbackId = `${id}-feedback`;
  const helpTextId = `${id}-helptext`;
  return { id, labelId, feedbackId, helpTextId };
};
var useFormCallbacks = (props, {
  generatedId,
  labelId,
  feedbackId,
  helpTextId
}) => {
  const { isInvalid, isDisabled, isReadOnly, isHidden, isValid, isRequired } = props;
  const [isFeedbackTextVisible, { on: toggleFeedbackText }] = useToggle(false);
  const [isHelpTextVisible, { on: toggleHelpText }] = useToggle(false);
  const [isCharacterCountVisible, { on: toggleCharacterCount }] = useToggle(false);
  const [elementRef, setElementRef] = React2.useState();
  const [internalInputElementValue, setInternalInputElementValue] = React2.useState();
  const dataAttributes = React2.useCallback(
    (elementProps = {}) => {
      var _a, _b, _c, _d, _e, _f, _g, _h;
      const disabled = (_b = (_a = elementProps.disabled) != null ? _a : elementProps.isDisabled) != null ? _b : isDisabled;
      const readOnly = (_d = (_c = elementProps.readOnly) != null ? _c : elementProps.isReadOnly) != null ? _d : isReadOnly;
      const hidden = (_f = (_e = elementProps.hidden) != null ? _e : elementProps.isHidden) != null ? _f : isHidden;
      const invalid = (_g = elementProps.isInvalid) != null ? _g : isInvalid;
      const valid = (_h = elementProps.isValid) != null ? _h : isValid;
      return {
        ...disabled ? { "data-disabled": "" } : void 0,
        ...invalid ? { "data-invalid": "" } : void 0,
        ...valid ? { "data-valid": "" } : void 0,
        ...readOnly ? { "data-readonly": "" } : void 0,
        ...hidden ? { "data-hidden": "" } : void 0
      };
    },
    [isDisabled, isInvalid, isReadOnly, isHidden, isValid]
  );
  const formHelpTextProps = React2.useCallback(
    (helpTextProps = {
      preventRegistration: false
    }, forwardedRef = null) => {
      var _a;
      return {
        id: (_a = helpTextProps.id) != null ? _a : helpTextId,
        ...helpTextProps,
        ...dataAttributes(helpTextProps),
        ref: mergeRefs(forwardedRef, (element) => {
          if (!element) return;
          !helpTextProps.preventRegistration && toggleHelpText();
        })
      };
    },
    [helpTextId, dataAttributes, toggleHelpText]
  );
  const formFeedbackTextProps = React2.useCallback(
    (formFeedbackTextProps2 = {}, forwardedRef = null) => {
      var _a;
      return {
        id: (_a = formFeedbackTextProps2.id) != null ? _a : feedbackId,
        ...formFeedbackTextProps2,
        ...dataAttributes(formFeedbackTextProps2),
        "aria-live": "polite",
        ref: mergeRefs(forwardedRef, (element) => {
          if (!element) return;
          toggleFeedbackText();
        })
      };
    },
    [feedbackId, dataAttributes, toggleFeedbackText]
  );
  const formLabelProps = React2.useCallback(
    (labelProps = {}, forwardedRef = null) => {
      var _a, _b;
      return {
        ...labelProps,
        ref: forwardedRef,
        ...dataAttributes(labelProps),
        id: (_a = labelProps.id) != null ? _a : labelId,
        htmlFor: (_b = labelProps.htmlFor) != null ? _b : generatedId
      };
    },
    [generatedId, dataAttributes, labelId]
  );
  const formElementFocus = React2.useCallback(
    (props2 = {}, forwardedRef = null) => {
      var _a;
      return {
        ...props2,
        ref: forwardedRef,
        disabled: (_a = props2.disabled) != null ? _a : isDisabled,
        tabIndex: isDisabled || props2.disabled ? -1 : 0,
        onClick: (event) => {
          var _a2;
          const correctForwardedRef = isFunction(forwardedRef) ? forwardedRef == null ? void 0 : forwardedRef.current : forwardedRef;
          const realRef = (_a2 = correctForwardedRef != null ? correctForwardedRef : elementRef) != null ? _a2 : void 0;
          if (realRef) {
            realRef.focus();
            const selectionStart = realRef.selectionStart;
            const selectionEnd = realRef.selectionEnd;
            const selectionDirection = realRef.selectionDirection;
            if (realRef === document.activeElement && selectionStart && selectionEnd && selectionDirection) {
              setTimeout(function() {
                realRef.setSelectionRange(
                  selectionStart,
                  selectionEnd,
                  selectionDirection
                );
              }, 0);
            }
          }
          props2.onClick && props2.onClick(event);
        }
      };
    },
    [elementRef, isDisabled]
  );
  const formCharacterCount = React2.useCallback(
    (props2 = {}, forwardedRef = null) => {
      var _a, _b, _c;
      return {
        ...props2,
        preventRegistration: true,
        id: (_a = props2.id) != null ? _a : `${helpTextId}-word-count`,
        ref: mergeRefs(
          (_b = props2.ref) != null ? _b : forwardedRef,
          (element) => {
            if (!element) return;
            toggleCharacterCount();
            if (elementRef) {
              setInternalInputElementValue(elementRef.value);
              elementRef.maxLength = Number(
                element.getAttribute("data-character-limit")
              );
            }
          }
        ),
        inputProps: {
          value: internalInputElementValue,
          maxLength: (_c = elementRef && elementRef.maxLength) != null ? _c : void 0
        },
        inputChangeHandler: {
          onChange: (event) => execAllFunctions(
            () => {
              if (elementRef) {
                setInternalInputElementValue(elementRef.value);
              }
              return;
            },
            ([event2]) => props2.onChange && props2.onChange(event2)
          )(event)
        }
      };
    },
    [
      elementRef,
      toggleCharacterCount,
      helpTextId,
      setInternalInputElementValue,
      internalInputElementValue
    ]
  );
  const formElementProps = React2.useCallback(
    (elementProps = {}, forwardedRef = null) => {
      var _a, _b, _c, _d, _e, _f, _g, _h, _i;
      const { id } = elementProps;
      const hidden = (_b = (_a = elementProps.hidden) != null ? _a : elementProps.isHidden) != null ? _b : isHidden;
      const disabled = (_d = (_c = elementProps.disabled) != null ? _c : elementProps.isDisabled) != null ? _d : isDisabled;
      const readOnly = (_f = (_e = elementProps.readOnly) != null ? _e : elementProps.isReadOnly) != null ? _f : isReadOnly;
      const required = (_h = (_g = elementProps.required) != null ? _g : elementProps.isRequired) != null ? _h : isRequired;
      const invalid = (_i = elementProps.isInvalid) != null ? _i : isInvalid;
      const ariaDescribedBy = [
        isFeedbackTextVisible && (isInvalid || isValid) && feedbackId,
        isHelpTextVisible && helpTextId,
        isCharacterCountVisible && `${helpTextId}-word-count`
      ].filter((item) => item).join(" ");
      return {
        ...elementProps,
        // Set the element ref merged with the one supplied. This allows the input to work with or without context
        ref: mergeRefs(forwardedRef, (element) => {
          if (!element) return;
          setElementRef(element);
        }),
        id: id != null ? id : generatedId,
        hidden,
        disabled,
        readOnly,
        required,
        // https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/ARIA_Techniques/Using_the_aria-labelledby_attribute
        ...ariaDescribedBy.length > 0 ? { "aria-describedby": ariaDescribedBy } : void 0,
        ...hidden ? { "aria-hidden": true } : void 0,
        ...invalid ? { "aria-invalid": true } : void 0,
        ...required ? { "aria-required": true } : void 0,
        ...readOnly ? { "aria-readonly": true } : void 0
      };
    },
    [
      generatedId,
      isDisabled,
      isHidden,
      isInvalid,
      isValid,
      isReadOnly,
      isRequired,
      feedbackId,
      helpTextId,
      isFeedbackTextVisible,
      isHelpTextVisible,
      isCharacterCountVisible
    ]
  );
  return {
    elementProps: {
      dataAttributes,
      formCharacterCount,
      formElementFocus,
      formElementProps,
      formFeedbackTextProps,
      formHelpTextProps,
      formLabelProps
    },
    elementState: {
      isHelpTextVisible,
      isFeedbackTextVisible,
      isCharacterCountVisible
    }
  };
};
var useFormStateHandler = (props) => {
  const {
    id,
    isDisabled,
    isInvalid,
    isValid,
    isReadOnly,
    isRequired,
    isHidden,
    size
  } = props;
  const {
    id: generatedId,
    labelId,
    feedbackId,
    helpTextId
  } = useFormIdentifiers(id);
  const { elementProps, elementState } = useFormCallbacks(props, {
    generatedId,
    labelId,
    feedbackId,
    helpTextId
  });
  return {
    // flags
    isRequired: Boolean(isRequired),
    isInvalid: Boolean(isInvalid),
    isValid: Boolean(isValid),
    isReadOnly: Boolean(isReadOnly),
    isDisabled: Boolean(isDisabled),
    isHidden: Boolean(isHidden),
    // ids
    id: generatedId,
    labelId,
    feedbackId,
    helpTextId,
    size,
    // elementProps
    ...elementProps,
    // element state tracker
    ...elementState
  };
};

// src/styles.ts
var root = {
  position: "relative",
  width: "100%"
};
var styles = {
  root
};

// src/state-handler.tsx
var FormStateHandler = systemForwardRef(function FormStateHandler2({ children, ...props }, ref) {
  const { id, ...rest } = props;
  const rootStyles = Object.assign({}, styles.root);
  const context = useFormStateHandler(props);
  const formStateContext = React3.useMemo(() => context, [context]);
  return /* @__PURE__ */ React3.createElement(FormStateHandlerContext.Provider, { value: formStateContext }, /* @__PURE__ */ React3.createElement(Base, { ref, __root: rootStyles, role: "group", ...rest }, children));
});
var FormFieldControl = React3.memo(
  FormStateHandler
);
export {
  FormFieldControl,
  FormStateHandler,
  FormStateHandlerContext,
  useFormStateHandler,
  useFormStateHandlerContext
};
